import React from 'react';
import { DollarSign, Users, Rocket, Gift, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import Navbar from './Navbar';
import Footer from './Footer';

const AffiliatePage = () => {
  const targetAudience = [
    'Empreendedores',
    'Especialistas em Marketing',
    'Influencers',
    'Consultores de tecnologia',
    'Agências digitais',
    'Consultores de negócios',
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      <main className="pt-28 pb-16">
        {/* Hero */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Ganhe até <span className="text-[#3b82f6]">50% de comissão</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Junte-se como afiliado e ganhe entre 30% e 50% de comissão por cada cliente durante 12 meses.
          </p>
          <Button size="lg" className="bg-[#3b82f6] hover:bg-[#2563eb] text-lg px-8 py-6 h-auto">
            Participar do Programa
          </Button>
        </div>

        {/* Benefits */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-4 gap-6">
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 bg-[#dbeafe] rounded-full flex items-center justify-center mx-auto mb-4">
                    <DollarSign className="h-7 w-7 text-[#3b82f6]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">30-50% Comissão</h3>
                  <p className="text-sm text-gray-600">Por cada cliente durante 12 meses</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 bg-[#dbeafe] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Rocket className="h-7 w-7 text-[#3b82f6]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Renda Recorrente</h3>
                  <p className="text-sm text-gray-600">Comissões mensais contínuas</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 bg-[#dbeafe] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="h-7 w-7 text-[#3b82f6]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Materiais Exclusivos</h3>
                  <p className="text-sm text-gray-600">Recursos de marketing prontos</p>
                </CardContent>
              </Card>
              <Card className="text-center">
                <CardContent className="pt-6">
                  <div className="w-14 h-14 bg-[#dbeafe] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="h-7 w-7 text-[#3b82f6]" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">Suporte Dedicado</h3>
                  <p className="text-sm text-gray-600">Equipe para ajudar você</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* Calculator */}
        <div className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-8">Quanto você pode ganhar?</h2>
            <Card className="bg-gradient-to-br from-[#3b82f6] to-[#1d4ed8] text-white">
              <CardContent className="py-12">
                <div className="grid md:grid-cols-3 gap-8 mb-8">
                  <div>
                    <p className="text-blue-200 mb-2">Usuários indicados</p>
                    <p className="text-4xl font-bold">100</p>
                  </div>
                  <div>
                    <p className="text-blue-200 mb-2">Período</p>
                    <p className="text-4xl font-bold">12 meses</p>
                  </div>
                  <div>
                    <p className="text-blue-200 mb-2">Ganhos estimados</p>
                    <p className="text-4xl font-bold">R$57.000</p>
                  </div>
                </div>
                <Button variant="secondary" size="lg" className="bg-white text-[#3b82f6] hover:bg-gray-100">
                  Calcular meus ganhos
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Who is it for */}
        <div className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Para quem é o Programa?</h2>
            <div className="grid md:grid-cols-3 gap-4">
              {targetAudience.map((audience) => (
                <div key={audience} className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm">
                  <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span className="font-medium text-gray-900">{audience}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* How it works */}
        <div className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">Como funciona</h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">1</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cadastre-se</h3>
                <p className="text-gray-600">Registre-se e receba seu link único de afiliado.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">2</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Compartilhe</h3>
                <p className="text-gray-600">Promova em blogs, vídeos e redes sociais.</p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-[#3b82f6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-white">3</span>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Ganhe!</h3>
                <p className="text-gray-600">Receba 30-50% de comissão por 12 meses.</p>
              </div>
            </div>
            <div className="text-center mt-12">
              <Button size="lg" className="bg-[#3b82f6] hover:bg-[#2563eb]">
                Começar Agora
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AffiliatePage;
